import * as React from "react";
import { observer, inject } from "mobx-react";

import { HorseForSaleStore } from "../../stores/HorseForSaleStore";
import { CustomerStore } from "../../stores/CustomerStore";
import { ConfigurationStore } from "../../stores/ConfigurationStore";
import { CustomerEndpoints } from "../../endpoints/CustomerEndpoints";

import { Contact, Business, ExtractFriendlyName, SimpleCustomer, BusinessContactRelationship, CustomerTypeWithBoth } from "../../model/Customer";
import { HorseForSaleOwnership } from "src/model/HorseForSale";

import { GeocodeAddress } from "src/common/Methods/Geocoder";
import EditableCard, { WhenEditable, WhenNotEditable, EditableSubCard, EditableInputGroup } from "../common/EditableCard";

import { CustomerLink } from "../common/CustomerLink";
import CustomerSelect from "../common/CustomerSelect";
import { SimpleConfirmation, SimpleAlert } from "../common/SimpleConfirmAlert";
import LoadingWidget from "../common/LoadingWidget";
import AttachmentLogPanel from "../common/AttachmentLogPanel";
import MapContainer from "../common/GoogleMaps";

import OwnersApplication from "./OwnersApplication";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt, faMapPin, faMapMarkedAlt } from "@fortawesome/free-solid-svg-icons";
import { successMessage, errorMessage } from "../common/Toast";

const NONE_ID: number = -1;

type Coordinates = { lat: number; lng: number };

export interface Props {
    configurationStore?: ConfigurationStore;
    horseForSaleStore?: HorseForSaleStore;
    customerStore?: CustomerStore;
    customerEndpoints?: CustomerEndpoints;
}

export interface Owners {
    owner: SimpleCustomer;
    percentage: number;
}

export interface ComponentState {
    isEditable: boolean;
    isLoading: boolean;
    wasValidated: boolean;
    consignorCatalogNameChange: boolean;
    raisedByCatalogNameChange: boolean;
    currentRelationship: string;
}

export interface EditableFields {
    owners: Array<HorseForSaleOwnership> | null;

    consignor: SimpleCustomer | null;
    consignorCatalogName: string;

    raisedBy: SimpleCustomer | null;
    raisedByCatalogName: string;

    contact: SimpleCustomer | null;

    address911: string;
    countryId911: number;
    provinceId911: number;
    city911: string;
    postalCode911: string;
    latitude: number | null;
    longitude: number | null;

    signatureOf: string;
    signatureContact: SimpleCustomer | null;

    enrollmentInFullCircle: string;

    damHipNumberName: string;
}

export type ApplicationState = ComponentState & EditableFields;

@inject("customerStore")
@inject("configurationStore")
@inject("horseForSaleStore")
@inject("customerEndpoints")
@observer
export default class Application extends React.Component<Props, ApplicationState> {
    constructor(props: Props) {
        super(props);
        this.state = {
            isEditable: false,
            isLoading: false,
            wasValidated: false,
            currentRelationship: "",
            owners: [],

            consignor: null,
            consignorCatalogName: "",
            consignorCatalogNameChange: false,

            raisedBy: null,
            raisedByCatalogName: "",
            raisedByCatalogNameChange: false,

            contact: null,

            address911: "",
            countryId911: NONE_ID,
            provinceId911: NONE_ID,
            city911: "",
            postalCode911: "",
            latitude: null,
            longitude: null,

            signatureOf: "OWNER",
            signatureContact: null,

            enrollmentInFullCircle: "false",

            damHipNumberName: ""
        };
    }

    // Form Data Manipulation
    editFieds = () => {
        this.setState(this.copyHorseForSaleToState({ isEditable: true }) as any);
    };

    saveEditableFields = () => {
        this.props
            .horseForSaleStore!.updateFieldsByObject(this.buildFieldsToUpdate())
            .then(() => {
                successMessage("Changes successfully saved.");
                this.setState({ isEditable: false });
            })
            .catch(error => errorMessage(error));
    };

    cancelEditableFields = () => {
        this.setState({ isEditable: false });
    };

    //Basic Form
    handleOnChange = (
        event:
            | React.ChangeEvent<HTMLInputElement>
            | React.ChangeEvent<HTMLSelectElement>
            | React.ChangeEvent<HTMLTextAreaElement>
            | React.ChangeEvent<HTMLSelectElement>
    ): void => {
        event.preventDefault();

        const target = event.target;
        const value = target.value;
        const name = target.name;

        const state = this.state;
        state[name] = value;
        this.setState(state);
    };

    onSelectProvince = (e: React.ChangeEvent<HTMLSelectElement>): void => {
        e.preventDefault();
        const provinceId911 = parseInt(e.target.value, 10);
        this.setState({ provinceId911 });
    };

    onSelectCountry = (e: React.ChangeEvent<HTMLSelectElement>): void => {
        e.preventDefault();
        const countryId911 = parseInt(e.target.value, 10);
        this.setState({ countryId911, provinceId911: NONE_ID });
    };

    submitApplication = (e: React.FormEvent<HTMLFormElement>): void => {
        e.preventDefault();
        this.saveEditableFields();
    };

    toggleState = (name: string) => {
        const state = this.state;
        state[name] = !state[name];
        this.setState(state);
    };

    handleSignatureOf = (e: any) => {
        const signatureOf = e.currentTarget.value;
        this.setState({ signatureOf });
    };

    handleFullCicleProgram = (e: any) => {
        const enrollmentInFullCircle = e.currentTarget.value;
        this.setState({ enrollmentInFullCircle });
    };

    copyAddressRaisedBy = () => {
        if (this.state.raisedBy && this.state.raisedBy.address) {
            const address = this.state.raisedBy.address;
            if (address.address) {
                this.setState({ address911: address.address });
            }
            if (address.country) {
                this.setState({ countryId911: address.country.id });
            }
            if (address.province) {
                this.setState({ provinceId911: address.province.id });
            }
            if (address.city) {
                this.setState({ city911: address.city });
            }
            if (address.postalCode) {
                this.setState({ postalCode911: address.postalCode });
            }
        } else {
            this.setState({ address911: "", countryId911: NONE_ID, provinceId911: NONE_ID, city911: "", postalCode911: "" });
        }
    };

    getGeocode = () => {
        const configurationStore = this.props.configurationStore!;

        const address = this.state.address911;
        const city = this.state.city911;
        const country = this.state.countryId911 !== NONE_ID ? configurationStore.countries.find(c => c.id === this.state.countryId911) : undefined;
        const province = this.state.provinceId911 !== NONE_ID ? configurationStore.provinces.find(p => p.id === this.state.provinceId911) : undefined;

        GeocodeAddress({ address, city, country, province })
            .then(coordinates => this.setState({ latitude: coordinates.latitude, longitude: coordinates.longitude }))
            .catch(err => errorMessage(err));
    };

    render() {
        const { horseForSaleStore } = this.props;
        if (!horseForSaleStore) {
            throw new Error(" ** No Horse For Sale Store **");
        }

        const horseForSale = horseForSaleStore.horseForSale;
        if (!horseForSale) {
            return <div />;
        }

        const { configurationStore } = this.props;
        if (!configurationStore) {
            throw new Error(" ** No Configuration Store **");
        }

        const countryValue =
            (this.state.isEditable
                ? this.state.countryId911
                : horseForSale.address911 && horseForSale.address911.country && horseForSale.address911.country.id) || NONE_ID;

        const provinceValue =
            (this.state.isEditable
                ? this.state.provinceId911
                : horseForSale.address911 && horseForSale.address911.province && horseForSale.address911.province.id) || NONE_ID;

        const enrollmentInFullCircleValue = this.state.isEditable
            ? this.state.enrollmentInFullCircle === "false"
                ? false
                : true
            : horseForSale.enrollmentInFullCircle;

        const signatureOfAgent = this.state.isEditable ? this.state.signatureOf === "AGENT" : horseForSale.signatureOf === "AGENT";

        const signatureOfOwner = this.state.isEditable ? this.state.signatureOf === "OWNER" : horseForSale.signatureOf === "OWNER";

        return (
            <>
                <EditableCard
                    title="Application"
                    extraClassName="editable-card-header"
                    editable={this.state.isEditable}
                    onEdit={this.editFieds}
                    onCancel={this.cancelEditableFields}
                    onSave={this.saveEditableFields}
                >
                    <OwnersApplication
                        isEditable={this.state.isEditable}
                        owners={this.state.isEditable ? this.state.owners : horseForSale.owners}
                        onChange={(owners: Array<HorseForSaleOwnership>) => this.setState({ owners })}
                    />

                    <form id="application-form" onSubmit={e => this.submitApplication(e)}>
                        {this.commonCustomerSelection("Consignor", "consignor", "BOTH")}
                        {this.commonCustomerSelection("Raised By", "raisedBy", "BOTH")}
                        {this.commonCustomerSelection("Contact Name (For Inspection)", "contact", "CONTACT", false)}

                        <EditableSubCard
                            label={
                                <div className="row">
                                    <div className="col-8">911 Address (Site for Inspection)</div>
                                    <div className="col-4 text-right pr-0 mr-0">
                                        {this.state.isEditable && (
                                            <button
                                                type="button"
                                                className="btn btn-info btn-sm"
                                                data-toggle="button"
                                                aria-pressed="false"
                                                disabled={!this.state.raisedBy}
                                                onClick={this.copyAddressRaisedBy}
                                            >
                                                Copy From Raised By
                                            </button>
                                        )}
                                    </div>
                                </div>
                            }
                        >
                            <div className="form-row mb-3">
                                <EditableInputGroup
                                    label="Street Address:"
                                    name="address911"
                                    type="text"
                                    className="col-md-12 col-lg-8"
                                    inputClassName="not-required"
                                    editable={this.state.isEditable}
                                    editableValue={this.state.address911}
                                    nonEditableValue={(horseForSale.address911 && horseForSale.address911.address) || ""}
                                    onChange={this.handleOnChange}
                                />
                                <EditableInputGroup
                                    label="City:"
                                    name="city911"
                                    type="text"
                                    className="col-md-12 col-lg-4"
                                    inputClassName="not-required"
                                    editable={this.state.isEditable}
                                    editableValue={this.state.city911}
                                    nonEditableValue={(horseForSale.address911 && horseForSale.address911.city) || ""}
                                    onChange={this.handleOnChange}
                                />
                            </div>
                            <div className="form-row mb-3">
                                <EditableInputGroup
                                    label="Postal Code:"
                                    name="postalCode911"
                                    type="text"
                                    className="col-md-12 col-lg-4"
                                    inputClassName="not-required"
                                    editable={this.state.isEditable}
                                    editableValue={this.state.postalCode911}
                                    nonEditableValue={(horseForSale.address911 && horseForSale.address911.postalCode) || ""}
                                    onChange={this.handleOnChange}
                                />
                                <div className="form-group col-md-12 col-lg-4">
                                    <label htmlFor="countryId911">Country: </label>
                                    <select
                                        className="form-control not-required"
                                        name="countryId911"
                                        disabled={!this.state.isEditable}
                                        onChange={this.onSelectCountry}
                                        value={countryValue}
                                    >
                                        {countryValue === NONE_ID && <option value={NONE_ID}>Select a country ...</option>}
                                        {configurationStore.countries
                                            .slice()
                                            .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
                                            .map(c => (
                                                <option key={c.id} value={c.id}>
                                                    {c.name} ({c.abbreviation})
                                                </option>
                                            ))}
                                    </select>
                                </div>
                                <div className="form-group col-md-12 col-lg-4">
                                    <label htmlFor="provinceId911">State or Province: </label>
                                    <select
                                        className="form-control not-required"
                                        name="provinceId911"
                                        disabled={!this.state.isEditable}
                                        onChange={this.onSelectProvince}
                                        value={provinceValue}
                                    >
                                        {countryValue === NONE_ID && provinceValue === NONE_ID && (
                                            <option value={NONE_ID}>Select a Province ...</option>
                                        )}
                                        {configurationStore.provinces
                                            .filter(p => p.countryId === countryValue)
                                            .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
                                            .map(c => (
                                                <option key={c.id} value={c.id}>
                                                    {c.name} ({c.abbreviation})
                                                </option>
                                            ))}
                                    </select>
                                </div>
                            </div>
                            <div className="form-row mb-3">
                                <div className="form-group col-md-12">
                                    {this.state.isEditable && (
                                        <button
                                            type="button"
                                            className="btn btn-info mr-2"
                                            data-toggle="button"
                                            aria-pressed="false"
                                            disabled={
                                                !this.state.isEditable &&
                                                (this.state.address911 !== "" ||
                                                    this.state.city911 !== "" ||
                                                    this.state.postalCode911 !== "" ||
                                                    this.state.countryId911 !== NONE_ID ||
                                                    this.state.provinceId911 !== NONE_ID)
                                            }
                                            onClick={this.getGeocode}
                                        >
                                            <FontAwesomeIcon icon={faMapMarkedAlt} className="mr-2" />
                                            Geocode Address
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="form-row mb-3">
                                <div
                                    className="form-group col-md-12"
                                    style={{ width: "100%", height: "300px", backgroundColor: "#f4f4f4", textAlign: "center" }}
                                >
                                    {this.determineCurrentCoordinates() !== null && (
                                        <MapContainer
                                            onMarkerLocationChange={(latitude, longitude) => this.setState({ latitude, longitude })}
                                            disabled={!this.state.isEditable}
                                            mapCentre={this.determineCurrentCoordinates() as Coordinates}
                                            markerLocation={this.determineCurrentCoordinates() as Coordinates}
                                        />
                                    )}
                                </div>
                            </div>
                        </EditableSubCard>

                        <EditableSubCard label="Signature, Dam's Hip # Generation Name, Full Circle">
                            <div className="form-row">
                                <div className="form-group col-sm-12 col-md-3">
                                    <label className="d-block" htmlFor="selectedOwnerContact">
                                        {" "}
                                        Signature Of:
                                    </label>
                                    <div className="btn-group btn-group-toggle" data-toggle="buttons">
                                        <label
                                            className={
                                                "btn " +
                                                (signatureOfOwner ? " btn-info " : " btn-outline-info ") +
                                                (!this.state.isEditable ? " disabled " : "")
                                            }
                                        >
                                            <input
                                                type="radio"
                                                name="signatureOf"
                                                id="option1"
                                                value="OWNER"
                                                checked={signatureOfOwner}
                                                onChange={this.handleSignatureOf}
                                                disabled={!this.state.isEditable}
                                            />{" "}
                                            Owner
                                        </label>
                                        <label
                                            className={
                                                "btn " +
                                                (signatureOfAgent ? " btn-info " : " btn-outline-info ") +
                                                (!this.state.isEditable ? " disabled " : "")
                                            }
                                        >
                                            <input
                                                type="radio"
                                                name="signatureOf"
                                                value="AGENT"
                                                checked={signatureOfAgent}
                                                onChange={this.handleSignatureOf}
                                                disabled={!this.state.isEditable}
                                            />{" "}
                                            Agent
                                        </label>
                                    </div>
                                </div>
                                <div className="form-group col-md-12 col-lg-9">
                                    <label htmlFor="catalog-name">Signature Contact:</label>
                                    <WhenEditable>
                                        <CustomerSelect
                                            isDisabledLink={!this.state.isEditable}
                                            customerEndpoints={this.props.customerEndpoints!}
                                            className="mb-3"
                                            include="CONTACT"
                                            showCreate={true}
                                            onCreate={signatureContact => this.setState({ signatureContact })}
                                            value={this.state.isEditable ? this.state.signatureContact : horseForSale.signatureContact || null}
                                            onChange={signatureContact => this.setState({ signatureContact })}
                                        />
                                    </WhenEditable>
                                    <WhenNotEditable>
                                        {!this.checkFieldNotNull("signatureContact") && <div className="text-muted"> - Not Specified - </div>}
                                    </WhenNotEditable>
                                    {this.checkFieldNotNull("signatureContact") && (
                                        <CustomerLink
                                            customer={this.state.isEditable ? this.state.signatureContact! : horseForSale.signatureContact!}
                                            className="btn btn-info btn-sm btn-block py-2"
                                            includeAddress={true}
                                            includePhone={true}
                                            includeEmail={true}
                                            icons={true}
                                        />
                                    )}
                                </div>
                            </div>
                            <div className="form-row mt-2">
                                {horseForSale.horse && horseForSale.horse.dam && (
                                    <EditableInputGroup
                                        label="Dam's Hip # Generation Name:"
                                        name="damHipNumberName"
                                        type="text"
                                        className="col-md-8 col-sm-12"
                                        inputClassName="not-required"
                                        editable={this.state.isEditable}
                                        editableValue={this.state.damHipNumberName}
                                        nonEditableValue={horseForSale.horse.dam.hipNumberName || ""}
                                        onChange={this.handleOnChange}
                                        smallText={
                                            <span>
                                                <strong>NOTE: </strong>The Dam of <em>{horseForSale.horse.name}</em>&nbsp; is{" "}
                                                <em>{horseForSale.horse.dam.name}</em>
                                            </span>
                                        }
                                    />
                                )}
                                {(!horseForSale.horse || !horseForSale.horse.dam) && (
                                    <div className="col-md-8 col-sm-12">
                                        <div className="alert alert-warning">
                                            The current Horse does not have a Dam specified. A Dam Hip # Generation Name cannot be specified as a
                                            result.
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="form-row mt-3">
                                <div className="form-group col-sm-12 col-md-12">
                                    <label htmlFor="" className="d-block">
                                        Enrollment in the Full Circle Program:{" "}
                                    </label>
                                    <div className="btn-group btn-group-toggle disabled" data-toggle="buttons">
                                        <label
                                            className={
                                                "btn " +
                                                (enrollmentInFullCircleValue ? "btn-info" : "btn-outline-info") +
                                                (!this.state.isEditable ? " disabled " : "")
                                            }
                                        >
                                            <input
                                                type="radio"
                                                name="enrollmentInFullCircle"
                                                id="option1"
                                                value="true"
                                                checked={enrollmentInFullCircleValue}
                                                onChange={this.handleFullCicleProgram}
                                                disabled={!this.state.isEditable}
                                            />{" "}
                                            Yes
                                        </label>
                                        <label
                                            className={
                                                "btn " +
                                                (!enrollmentInFullCircleValue ? "btn-info" : "btn-outline-info") +
                                                (!this.state.isEditable ? " disabled " : "")
                                            }
                                        >
                                            <input
                                                type="radio"
                                                name="enrollmentInFullCircle"
                                                value="false"
                                                checked={!enrollmentInFullCircleValue}
                                                onChange={this.handleFullCicleProgram}
                                                disabled={!this.state.isEditable}
                                            />{" "}
                                            No
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </EditableSubCard>
                        <input type="submit" form="application-form" style={{ display: "none" }} />
                    </form>
                </EditableCard>

                <AttachmentLogPanel
                    extraClassName="editable-card-header"
                    title="Application"
                    attachmentLog={horseForSale.attachments.applicationFiles}
                    onAddAttachment={horseForSaleStore.addAttachment.bind(horseForSaleStore)}
                    onDeleteAttachment={horseForSaleStore.deleteAttachment.bind(horseForSaleStore)}
                    isEditable={true}
                />
            </>
        );
    }

    private copyHorseForSaleToState(otherFields: Partial<ApplicationState> = {}): Partial<ApplicationState> {
        const horseForSale = this.props.horseForSaleStore!.horseForSale;
        if (!horseForSale) {
            return {};
        }

        const updatedState: Partial<ApplicationState> = Object.assign(
            {
                consignor: horseForSale.consignor || null,
                consignorCatalogName: horseForSale.consignorCatalogName || "",

                raisedBy: horseForSale.raisedBy || null,
                raisedByCatalogName: horseForSale.raisedByCatalogName || "",

                contact: horseForSale.contact || null,

                address911: (horseForSale.address911 && horseForSale.address911.address) || "",
                countryId911: (horseForSale.address911 && horseForSale.address911.country && horseForSale.address911.country.id) || NONE_ID,
                provinceId911: (horseForSale.address911 && horseForSale.address911.province && horseForSale.address911.province.id) || NONE_ID,
                city911: (horseForSale.address911 && horseForSale.address911.city) || "",
                postalCode911: (horseForSale.address911 && horseForSale.address911.postalCode) || "",
                latitude: (horseForSale.address911 && horseForSale.address911.latitude) || null,
                longitude: (horseForSale.address911 && horseForSale.address911.longitude) || null,

                signatureContact: horseForSale.signatureContact || null,
                signatureOf: horseForSale.signatureOf || "OWNER",
                enrollmentInFullCircle: horseForSale.enrollmentInFullCircle || "false",

                owners: horseForSale.owners || [],

                damHipNumberName: (horseForSale.horse && horseForSale.horse.dam && horseForSale.horse.dam.hipNumberName) || ""
            },
            otherFields
        );
        return updatedState;
    }

    private buildFieldsToUpdate(): { [field: string]: string | number | null } {
        const horseForSale = this.props.horseForSaleStore!.horseForSale;
        if (!horseForSale) {
            return {};
        }

        const obj: { [field: string]: string | number | null } = {};

        obj.address = this.state.address911;
        obj.city = this.state.city911;
        if (this.state.countryId911 !== parseInt("-1", 10)) {
            obj.country = this.state.countryId911;
        }
        if (this.state.provinceId911 !== parseInt("-1", 10)) {
            obj.province = this.state.provinceId911;
        }
        obj.postalcode = this.state.postalCode911;
        obj.latitude = this.state.latitude;
        obj.longitude = this.state.longitude;

        obj.consignor = this.state.consignor && this.state.consignor.id;
        obj.consignorCatalogName = this.state.consignorCatalogName;

        obj.contact = this.state.contact && this.state.contact.id;

        obj.raisedBy = this.state.raisedBy && this.state.raisedBy.id;
        obj.raisedByCatalogName = this.state.raisedByCatalogName;

        obj.signatureOf = this.state.signatureOf;
        obj.signatureContact = this.state.signatureContact && this.state.signatureContact.id;

        obj.enrollmentInFullCircle = this.state.enrollmentInFullCircle;

        obj.owners = JSON.stringify(this.prepareOwners());

        if (horseForSale.horse.dam) {
            obj.damHipNumberName = this.state.damHipNumberName;
        }

        return obj;
    }

    private prepareOwners(): { [customerId: number]: number } {
        const result = {};
        if (this.state.owners !== null) {
            this.state.owners.forEach(owner => {
                result[owner.owner.id] = owner.percentage;
            });
        }
        return result;
    }

    private checkFieldNotNull(name: string): boolean {
        if (this.state.isEditable) {
            return !!this.state[name];
        } else {
            return !!this.props.horseForSaleStore!.horseForSale![name];
        }
    }

    private determineCurrentCoordinates(): Coordinates | null {
        const horseForSale = this.props.horseForSaleStore!.horseForSale!;
        if (this.state.isEditable && this.state.latitude && this.state.longitude) {
            return { lat: this.state.latitude as number, lng: this.state.longitude as number };
        } else if (!this.state.isEditable && horseForSale.address911 && horseForSale.address911.latitude && horseForSale.address911.longitude) {
            return { lat: horseForSale.address911.latitude, lng: horseForSale.address911.longitude };
        }
        return null;
    }

    private commonCustomerSelection(label: string, fieldName: string, selectableTypes: CustomerTypeWithBoth, showCatalogName: boolean = true) {
        const horseForSale = this.props.horseForSaleStore!.horseForSale!;

        // We assume the following scheme to make our life easier
        const catalogFieldName = `${fieldName}CatalogName`;
        const catalogFieldNameChange = `${catalogFieldName}Change`;

        return (
            <EditableSubCard label={label}>
                <div className="form-row">
                    <div className="form-group col-sm-12 col-md-8">
                        <label htmlFor={`${fieldName}Contact`}>Name: </label>
                        <WhenEditable>
                            <CustomerSelect
                                isDisabledLink={!this.state.isEditable}
                                customerEndpoints={this.props.customerEndpoints!}
                                className="mb-3"
                                include={selectableTypes}
                                showCreate={true}
                                onCreate={contact => {
                                    const updatedState: Pick<ApplicationState, any> = {};
                                    updatedState[fieldName] = contact;
                                    if (contact) {
                                        updatedState[catalogFieldName] = ExtractFriendlyName(contact);
                                    } else {
                                        updatedState[catalogFieldName] = "";
                                    }
                                    console.log("OnChange", updatedState);
                                    this.setState(updatedState);
                                }}
                                noRedirect={true}
                                value={(this.state.isEditable ? this.state[fieldName] : horseForSale[fieldName]) || null}
                                onChange={contact => {
                                    const updatedState: Pick<ApplicationState, any> = {};
                                    updatedState[fieldName] = contact;
                                    if (contact) {
                                        updatedState[catalogFieldName] = ExtractFriendlyName(contact);
                                    } else {
                                        updatedState[catalogFieldName] = "";
                                    }
                                    this.setState(updatedState);
                                }}
                            />
                        </WhenEditable>
                        <WhenNotEditable>
                            {!this.checkFieldNotNull(fieldName) && <div className="text-muted"> - Not Specified - </div>}
                        </WhenNotEditable>
                        {/* Always show the link */}
                        {this.checkFieldNotNull(fieldName) && (
                            <CustomerLink
                                customer={this.state.isEditable ? this.state[fieldName] : horseForSale[fieldName]}
                                className="btn btn-info btn-sm btn-block py-2"
                                includeAddress={true}
                                includePhone={true}
                                includeEmail={true}
                                icons={true}
                            />
                        )}
                    </div>
                    {showCatalogName && (
                        <div className="form-group col-sm-12 col-md-4 mb-0">
                            <label htmlFor={catalogFieldName}>Catalog Name:</label>
                            {this.checkFieldNotNull(fieldName) && (
                                <>
                                    <input
                                        type="text"
                                        name={catalogFieldName}
                                        className="form-control not-required"
                                        placeholder="Name"
                                        disabled={!this.state[catalogFieldNameChange]}
                                        value={this.state.isEditable ? this.state[catalogFieldName] : horseForSale[catalogFieldName]}
                                        onChange={e => {
                                            e.preventDefault();
                                            const updatedState: Pick<ApplicationState, any> = {};
                                            updatedState[catalogFieldName] = e.target.value;
                                            this.setState(updatedState);
                                        }}
                                        required={true}
                                    />
                                    <WhenEditable>
                                        <div className="form-group form-check mt-1 mb-0">
                                            <input
                                                type="checkbox"
                                                className="form-check-input "
                                                name={catalogFieldNameChange}
                                                onChange={() => {
                                                    const updatedState: Pick<ApplicationState, any> = {};
                                                    if (this.state[catalogFieldNameChange]) {
                                                        updatedState[catalogFieldName] = this.state[fieldName]
                                                            ? ExtractFriendlyName(this.state[fieldName])
                                                            : "";
                                                        updatedState[catalogFieldNameChange] = false;
                                                    } else {
                                                        updatedState[catalogFieldName] = "";
                                                        updatedState[catalogFieldNameChange] = true;
                                                    }
                                                    this.setState(updatedState);
                                                }}
                                                checked={!this.state[catalogFieldNameChange]}
                                            />
                                            <label className="form-check-label" htmlFor="exampleCheck1">
                                                Use the same name
                                            </label>
                                        </div>
                                    </WhenEditable>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </EditableSubCard>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/horses-for-sale/Application.tsx